import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "Fractional CXO",
  "Consulting Manager / Principal",
  "Marketing / Growth Lead",
  "Sales / Business Development",
  "Strategy Analyst",
  "Internship",
  "Other",
];

const initial = { name: "", email: "", phone: "", role: "", message: "" };

export default function JoinUsDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => setForm(initial);
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and a short note.");
      return;
    }
    setSubmitting(true);
    try {
      const composed = `[CAREERS] Role interest: ${form.role || "Not specified"}\n\n${form.message}`;
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: null,
        message: composed,
        preferred_date: null,
        preferred_time: null,
      });
      track("join_us_submitted", { role: form.role || "unspecified" });
      toast.success("Thanks — we've received your application.", {
        description: "We'll reach out if there's a fit.",
      });
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        data-testid="join-dialog"
        className="max-w-2xl rounded-none border-zinc-200 p-0 gap-0 bg-white max-h-[92dvh] overflow-y-auto overscroll-contain"
      >
        <div className="px-8 pt-8 pb-4 border-b border-zinc-200">
          <DialogHeader className="text-left space-y-2">
            <div className="text-[10px] uppercase tracking-[0.28em] text-black font-semibold">
              Join Us
            </div>
            <DialogTitle className="font-serif-display text-3xl sm:text-4xl leading-tight font-normal">
              Work with BLEZZINGS.
            </DialogTitle>
            <DialogDescription className="text-zinc-600 text-sm">
              We&apos;re always keen to meet ambitious operators, strategists and
              CXOs. Tell us about yourself.
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="px-8 py-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Name*</Label>
              <Input data-testid="ju-name" value={form.name} onChange={set("name")} required placeholder="Full name" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Email*</Label>
              <Input data-testid="ju-email" type="email" value={form.email} onChange={set("email")} required placeholder="you@email.com" className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Phone</Label>
              <Input data-testid="ju-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black h-11" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Role of interest</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="ju-role" className="rounded-none border-zinc-300 h-11 focus:ring-0 focus:border-black">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Tell us about yourself*</Label>
            <Textarea data-testid="ju-message" value={form.message} onChange={set("message")} required rows={4} placeholder="A short note about your background, experience, and what you'd like to build with us. Feel free to paste your LinkedIn URL." className="rounded-none border-zinc-300 focus-visible:ring-0 focus-visible:border-black resize-none" />
          </div>

          <DialogFooter className="px-0 pt-2 sm:justify-between gap-3">
            <p className="text-xs text-zinc-500">
              We reply within one business week.
            </p>
            <button
              data-testid="ju-submit"
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 h-12 px-6 bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors duration-300 disabled:opacity-60"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 animate-spin" />Sending</>
              ) : (
                <>Send Application<ArrowUpRight className="h-4 w-4" /></>
              )}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
